import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateWorkingHoursThunk } from "../../../../store/thunks/adminThunks";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Checkbox from "../../../../components/common/Checkbox";
import {
  ClockIcon,
  PlusIcon,
  TrashIcon,
  DuplicateIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CalendarIcon,
} from "@heroicons/react/outline";

const WorkingHoursEditor = ({ workingHours: initialWorkingHours, admin }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.admin);

  // Days of the week
  const weekDays = [
    { id: "monday", label: "Monday", short: "Mon" },
    { id: "tuesday", label: "Tuesday", short: "Tue" },
    { id: "wednesday", label: "Wednesday", short: "Wed" },
    { id: "thursday", label: "Thursday", short: "Thu" },
    { id: "friday", label: "Friday", short: "Fri" },
    { id: "saturday", label: "Saturday", short: "Sat" },
    { id: "sunday", label: "Sunday", short: "Sun" },
  ];

  // Local state
  const [workingHours, setWorkingHours] = useState({});
  const [expandedDays, setExpandedDays] = useState({});
  const [errors, setErrors] = useState({});
  const [copiedDay, setCopiedDay] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize working hours - FIXED
  useEffect(() => {
    if (initialWorkingHours) {
      // Normalize the data - ensure all working days have at least one shift
      const normalizedHours = {};

      Object.keys(initialWorkingHours).forEach((day) => {
        const dayData = initialWorkingHours[day];

        // If working but no shifts, add a default shift
        if (
          dayData.isWorking &&
          (!dayData.shifts || dayData.shifts.length === 0)
        ) {
          normalizedHours[day] = {
            isWorking: true,
            shifts: [
              {
                start: "09:00",
                end: "17:00",
                breakStart: "",
                breakEnd: "",
              },
            ],
          };
        } else {
          normalizedHours[day] = { ...dayData };
        }
      });

      setWorkingHours(normalizedHours);

      // Expand days that are working
      const expanded = {};
      Object.keys(normalizedHours).forEach((day) => {
        if (normalizedHours[day]?.isWorking) {
          expanded[day] = true;
        }
      });
      setExpandedDays(expanded);
    } else {
      // Initialize with default structure
      const defaultHours = {};
      weekDays.forEach((day) => {
        defaultHours[day.id] = {
          isWorking: false,
          shifts: [],
        };
      });
      setWorkingHours(defaultHours);
    }
  }, [initialWorkingHours]); // Only re-run when initialWorkingHours changes

  // Track changes
  useEffect(() => {
    if (initialWorkingHours && Object.keys(workingHours).length > 0) {
      const changed =
        JSON.stringify(workingHours) !== JSON.stringify(initialWorkingHours);
      setHasChanges(changed);
    }
  }, [workingHours, initialWorkingHours]);

  // Toggle day working status - FIXED
  const toggleDayWorking = (dayId) => {
    setWorkingHours((prev) => {
      const newHours = { ...prev };
      const currentDay = {
        ...(newHours[dayId] || { isWorking: false, shifts: [] }),
      };

      // Toggle the working status
      currentDay.isWorking = !currentDay.isWorking;

      // If enabling and no shifts exist, add a default shift
      if (currentDay.isWorking && currentDay.shifts.length === 0) {
        currentDay.shifts = [
          {
            start: "09:00",
            end: "17:00",
            breakStart: "",
            breakEnd: "",
          },
        ];
      }

      // If disabling, clear shifts
      if (!currentDay.isWorking) {
        currentDay.shifts = [];
      }

      newHours[dayId] = currentDay;
      return newHours;
    });

    // Toggle expansion
    setExpandedDays((prev) => ({
      ...prev,
      [dayId]: !prev[dayId],
    }));
  };

  // Add shift to a day
  const addShift = (dayId) => {
    setWorkingHours((prev) => {
      const newHours = { ...prev };
      const currentDay = {
        ...(newHours[dayId] || { isWorking: true, shifts: [] }),
      };

      currentDay.shifts = [
        ...currentDay.shifts,
        {
          start: "09:00",
          end: "17:00",
          breakStart: "",
          breakEnd: "",
        },
      ];

      newHours[dayId] = currentDay;
      return newHours;
    });
  };

  // Remove shift from a day
  const removeShift = (dayId, shiftIndex) => {
    setWorkingHours((prev) => {
      const newHours = { ...prev };
      const currentDay = { ...newHours[dayId] };
      currentDay.shifts = currentDay.shifts.filter(
        (_, idx) => idx !== shiftIndex
      );

      // If removing last shift, disable the day
      if (currentDay.shifts.length === 0) {
        currentDay.isWorking = false;
      }

      newHours[dayId] = currentDay;
      return newHours;
    });
  };

  // Update shift time
  const updateShift = (dayId, shiftIndex, field, value) => {
    setWorkingHours((prev) => {
      const newHours = { ...prev };
      const currentDay = { ...newHours[dayId] };
      currentDay.shifts = [...currentDay.shifts];
      currentDay.shifts[shiftIndex] = {
        ...currentDay.shifts[shiftIndex],
        [field]: value,
      };
      newHours[dayId] = currentDay;
      return newHours;
    });

    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${dayId}-${shiftIndex}-${field}`];
      return newErrors;
    });
  };

  // Copy day schedule
  const copyDay = (dayId) => {
    setCopiedDay({
      dayId,
      data: { ...workingHours[dayId] },
    });
  };

  // Paste day schedule
  const pasteDay = (targetDayId) => {
    if (!copiedDay) return;

    setWorkingHours((prev) => ({
      ...prev,
      [targetDayId]: { ...copiedDay.data },
    }));

    setExpandedDays((prev) => ({
      ...prev,
      [targetDayId]: true,
    }));
  };

  // Apply to all working days
  const applyToAllWorkingDays = (sourceDayId) => {
    const sourceDay = workingHours[sourceDayId];
    if (!sourceDay) return;

    if (
      window.confirm(
        "Apply this schedule to all working days? This will overwrite existing schedules."
      )
    ) {
      setWorkingHours((prev) => {
        const newHours = { ...prev };
        Object.keys(newHours).forEach((dayId) => {
          if (newHours[dayId].isWorking && dayId !== sourceDayId) {
            newHours[dayId] = { ...sourceDay };
          }
        });
        return newHours;
      });
    }
  };

  // Validate working hours
  const validateWorkingHours = () => {
    const newErrors = {};

    Object.keys(workingHours).forEach((dayId) => {
      const day = workingHours[dayId];
      if (!day.isWorking) return;

      // Check if working day has shifts
      if (!day.shifts || day.shifts.length === 0) {
        newErrors[`${dayId}-no-shifts`] =
          "Working day must have at least one shift";
        return;
      }

      day.shifts.forEach((shift, shiftIndex) => {
        // Validate start and end times
        if (!shift.start || !shift.end) {
          newErrors[`${dayId}-${shiftIndex}-time`] =
            "Start and end times are required";
          return;
        }

        // Check if end time is after start time
        const [startHour, startMin] = shift.start.split(":").map(Number);
        const [endHour, endMin] = shift.end.split(":").map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        if (endMinutes <= startMinutes) {
          newErrors[`${dayId}-${shiftIndex}-time`] =
            "End time must be after start time";
        }

        // Validate break times if provided
        if (shift.breakStart && shift.breakEnd) {
          const [breakStartHour, breakStartMin] = shift.breakStart
            .split(":")
            .map(Number);
          const [breakEndHour, breakEndMin] = shift.breakEnd
            .split(":")
            .map(Number);
          const breakStartMinutes = breakStartHour * 60 + breakStartMin;
          const breakEndMinutes = breakEndHour * 60 + breakEndMin;

          if (breakEndMinutes <= breakStartMinutes) {
            newErrors[`${dayId}-${shiftIndex}-break`] =
              "Break end must be after break start";
          }

          if (
            breakStartMinutes < startMinutes ||
            breakEndMinutes > endMinutes
          ) {
            newErrors[`${dayId}-${shiftIndex}-break`] =
              "Break must be within shift hours";
          }
        }

        // Check for overlapping shifts
        day.shifts.forEach((otherShift, otherIndex) => {
          if (shiftIndex >= otherIndex) return;

          const [otherStartHour, otherStartMin] = otherShift.start
            .split(":")
            .map(Number);
          const [otherEndHour, otherEndMin] = otherShift.end
            .split(":")
            .map(Number);
          const otherStartMinutes = otherStartHour * 60 + otherStartMin;
          const otherEndMinutes = otherEndHour * 60 + otherEndMin;

          if (
            (startMinutes >= otherStartMinutes &&
              startMinutes < otherEndMinutes) ||
            (endMinutes > otherStartMinutes && endMinutes <= otherEndMinutes) ||
            (startMinutes <= otherStartMinutes && endMinutes >= otherEndMinutes)
          ) {
            newErrors[`${dayId}-${shiftIndex}-overlap`] =
              "Shifts cannot overlap";
          }
        });
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateWorkingHours()) {
      return;
    }

    try {
      await dispatch(
        updateWorkingHoursThunk({
          workingHours,
        })
      ).unwrap();
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to update working hours:", error);
    }
  };

  // Handle reset
  const handleReset = () => {
    if (window.confirm("Discard all changes?")) {
      setWorkingHours(initialWorkingHours || {});
      setErrors({});
      setHasChanges(false);
    }
  };

  // Render shift editor
  const renderShift = (dayId, shift, shiftIndex) => {
    const hasError = Object.keys(errors).some((key) =>
      key.startsWith(`${dayId}-${shiftIndex}`)
    );

    return (
      <div
        key={shiftIndex}
        className={`bg-gray-50 rounded-lg p-4 border ${
          hasError ? "border-red-300" : "border-gray-200"
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <p className="text-sm font-medium text-gray-700">
            Shift {shiftIndex + 1}
          </p>
          {workingHours[dayId].shifts.length > 1 && (
            <Button
              variant="danger-text"
              size="sm"
              onClick={() => removeShift(dayId, shiftIndex)}
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={shift.start}
              onChange={(e) =>
                updateShift(dayId, shiftIndex, "start", e.target.value)
              }
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              End Time
            </label>
            <input
              type="time"
              value={shift.end}
              onChange={(e) =>
                updateShift(dayId, shiftIndex, "end", e.target.value)
              }
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
        </div>

        {/* Break Times */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-700 mb-2">
            Break Time (Optional)
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <input
                type="time"
                value={shift.breakStart || ""}
                onChange={(e) =>
                  updateShift(dayId, shiftIndex, "breakStart", e.target.value)
                }
                placeholder="Break start"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
            <div>
              <input
                type="time"
                value={shift.breakEnd || ""}
                onChange={(e) =>
                  updateShift(dayId, shiftIndex, "breakEnd", e.target.value)
                }
                placeholder="Break end"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
          </div>
        </div>

        {/* Errors */}
        {Object.keys(errors)
          .filter((key) => key.startsWith(`${dayId}-${shiftIndex}`))
          .map((key) => (
            <div
              key={key}
              className="mt-2 text-xs text-red-600 flex items-center gap-1"
            >
              <ExclamationCircleIcon className="w-3 h-3" />
              {errors[key]}
            </div>
          ))}
      </div>
    );
  };

  // Render day card - FIXED
  const renderDayCard = (day) => {
    const dayData = workingHours[day.id] || { isWorking: false, shifts: [] };
    const isExpanded = expandedDays[day.id];
    const isWorking = dayData.isWorking;
    const hasShifts = dayData.shifts && dayData.shifts.length > 0;

    return (
      <div
        key={day.id}
        className={`border-2 rounded-lg overflow-hidden transition-all ${
          isWorking ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-white"
        }`}
      >
        {/* Day Header */}
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={isWorking}
                onChange={() => toggleDayWorking(day.id)}
              />
              <div>
                <p className="font-medium text-gray-900">{day.label}</p>
                {isWorking && hasShifts && (
                  <p className="text-sm text-gray-600">
                    {dayData.shifts.length} shift
                    {dayData.shifts.length > 1 ? "s" : ""}
                  </p>
                )}
                {isWorking && !hasShifts && (
                  <p className="text-xs text-red-600">No shifts configured</p>
                )}
              </div>
            </div>

            {isWorking && (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyDay(day.id)}
                  title="Copy this day's schedule"
                >
                  <DuplicateIcon className="w-4 h-4" />
                </Button>
                {copiedDay && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => pasteDay(day.id)}
                    title="Paste copied schedule"
                  >
                    📋
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setExpandedDays((prev) => ({
                      ...prev,
                      [day.id]: !prev[day.id],
                    }))
                  }
                >
                  {isExpanded ? "Hide" : "Edit"}
                </Button>
              </div>
            )}
          </div>

          {/* Quick Summary */}
          {isWorking && !isExpanded && hasShifts && (
            <div className="mt-3 flex flex-wrap gap-2">
              {dayData.shifts.map((shift, idx) => (
                <Badge key={idx} variant="info" className="text-xs">
                  {shift.start} - {shift.end}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Expanded Content */}
        {isWorking && isExpanded && (
          <div className="border-t p-4 bg-white space-y-3">
            {hasShifts ? (
              dayData.shifts.map((shift, shiftIndex) =>
                renderShift(day.id, shift, shiftIndex)
              )
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p className="mb-2">No shifts configured for this day</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => addShift(day.id)}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add First Shift
                </Button>
              </div>
            )}

            {/* Add Shift Button */}
            {hasShifts && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => addShift(day.id)}
                className="w-full"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Another Shift
              </Button>
            )}

            {/* Apply to All Button */}
            {hasShifts && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => applyToAllWorkingDays(day.id)}
                className="w-full"
              >
                Apply to All Working Days
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Calculate total working hours per week
  const calculateTotalHours = () => {
    let total = 0;
    Object.values(workingHours).forEach((day) => {
      if (day.isWorking && day.shifts) {
        day.shifts.forEach((shift) => {
          if (!shift.start || !shift.end) return;

          const [startHour, startMin] = shift.start.split(":").map(Number);
          const [endHour, endMin] = shift.end.split(":").map(Number);
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;
          let shiftMinutes = endMinutes - startMinutes;

          // Subtract break time
          if (shift.breakStart && shift.breakEnd) {
            const [breakStartHour, breakStartMin] = shift.breakStart
              .split(":")
              .map(Number);
            const [breakEndHour, breakEndMin] = shift.breakEnd
              .split(":")
              .map(Number);
            const breakStartMinutes = breakStartHour * 60 + breakStartMin;
            const breakEndMinutes = breakEndHour * 60 + breakEndMin;
            const breakMinutes = breakEndMinutes - breakStartMinutes;
            shiftMinutes -= breakMinutes;
          }

          total += shiftMinutes;
        });
      }
    });
    return (total / 60).toFixed(1);
  };

  // Show loading state if no data yet
  if (!workingHours || Object.keys(workingHours).length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading working hours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <ClockIcon className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Total Hours/Week</p>
              <p className="text-2xl font-bold text-gray-900">
                {calculateTotalHours()}h
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Working Days</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.values(workingHours).filter((d) => d.isWorking).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center gap-3">
            <DuplicateIcon className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Total Shifts</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.values(workingHours).reduce(
                  (sum, day) => sum + (day.shifts?.length || 0),
                  0
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Copy/Paste Indicator */}
      {copiedDay && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">
            📋 Schedule copied from{" "}
            <span className="font-medium">
              {weekDays.find((d) => d.id === copiedDay.dayId)?.label}
            </span>
            . Click the paste button on any day to apply.
          </p>
        </div>
      )}

      {/* Weekly Schedule */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-primary" />
          Weekly Schedule
        </h3>
        <div className="grid grid-cols-1 gap-3">
          {weekDays.map((day) => renderDayCard(day))}
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="sticky bottom-0 bg-white border-t pt-4 mt-6 flex justify-between items-center">
          <p className="text-sm text-orange-600 font-medium flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4" />
            You have unsaved changes
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={handleReset}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={loading || Object.keys(errors).length > 0}
            >
              {loading ? "Saving..." : "Save Working Hours"}
            </Button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Working Hours Tips</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>You can add multiple shifts per day for split schedules</li>
              <li>Break times are optional but recommended for accuracy</li>
              <li>Use copy/paste to quickly duplicate schedules across days</li>
              <li>Your status may auto-update based on these working hours</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursEditor;
